import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ophelos/ui/components/accordion';
import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Calendar } from '@ophelos/ui/components/calendar';
import { Input } from '@ophelos/ui/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@ophelos/ui/components/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ophelos/ui/components/tooltip';
import { Calendar as CalendarIcon, ChevronDown, ChevronRight, CircleAlert } from 'lucide-react';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { AffordabilityCommitment } from '@/components/payments/AffordabilityCommitment';
import { PayerEmailField } from '@/components/payments/PayerEmailField';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { PaymentPieChart } from '@/components/ui/PaymentPieChart';
import { usePayerEmail } from '@/hooks/usePayerEmail';
import { usePaymentDistributionPreview } from '@/hooks/usePaymentDistributionPreview';
import { useTranslation } from '@/hooks/useTranslation';
import { PaymentApiError, usePaymentApi } from '@/lib/payment-api';
import { buildScheduleForDate, toScheduleDate } from '@/lib/payment-schedule';
import { currencySymbol, formatMoneyInLanguage, toSubunits } from '@/lib/currency';
import { ACCOUNT_SCOPE, paymentReturnUrl } from '@/lib/paymentReturnUrl';
import type { DebtData } from '@/types';

interface AccountPartialPaymentTabProps {
  accountId: string | null;
  debts: DebtData[];
  totalBalance: number;
  allowDateSelect?: boolean; // If false, locks to "today"
  minPartialPaymentAmount?: number;
}

export function AccountPartialPaymentTab({ accountId, debts, totalBalance, allowDateSelect = false, minPartialPaymentAmount = 0 }: AccountPartialPaymentTabProps) {
  const { t, currentLanguage } = useTranslation();
  const { createPaymentSession } = usePaymentApi();
  const [partialAmount, setPartialAmount] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [open, setOpen] = useState(false);
  const [distributionOpen, setDistributionOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const { needsEmail, payerEmail, setPayerEmail, emailValid, persistPayerEmail } = usePayerEmail();

  const today = new Date();
  const maxDate = new Date();
  maxDate.setDate(today.getDate() + 14);

  const isToday =
    selectedDate &&
    selectedDate.getDate() === today.getDate() &&
    selectedDate.getMonth() === today.getMonth() &&
    selectedDate.getFullYear() === today.getFullYear();

  const parsedAmount = parseFloat(partialAmount) || 0;
  const isBelowMinimum = minPartialPaymentAmount > 0 && parsedAmount > 0 && parsedAmount < minPartialPaymentAmount;
  const isValidAmount = parsedAmount > 0 && parsedAmount < totalBalance && !isBelowMinimum;
  const currency = debts[0]?.currency ?? 'EUR';
  const {
    segments: previewSegments,
    isLoading: previewLoading,
    error: previewError,
  } = usePaymentDistributionPreview({
    accountId,
    amount: parsedAmount,
    currency,
    debts,
    enabled: distributionOpen && isValidAmount,
  });

  const handleContinue = async () => {
    if (!accountId || !isValidAmount || isProcessing) return;
    if (!emailValid) {
      setPaymentError(t('paymentPlan.emailRequired'));
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    try {
      // A future date turns the payment into a single-installment plan; today is
      // charged immediately. Scheduling is card-only for now — SEPA scheduling is
      // deferred.
      const isScheduled = !!selectedDate && !isToday;
      const baseUrl = window.location.origin;
      const successUrl = isScheduled
        ? paymentReturnUrl({ flow: 'scheduled', scope: ACCOUNT_SCOPE, date: toScheduleDate(selectedDate) })
        : paymentReturnUrl({ flow: 'immediate', scope: ACCOUNT_SCOPE });
      const cancelUrl = `${baseUrl}/account-payment`;
      const whenLabel = isScheduled ? ` on ${selectedDate.toLocaleDateString()}` : '';

      // Persist a customer-supplied email (when none usable on file) before checkout.
      await persistPayerEmail();

      const response = await createPaymentSession({
        account_id: accountId,
        amount_subunits: isScheduled ? undefined : toSubunits(parsedAmount),
        currency,
        payment_method: 'card',
        payment_type: 'partial',
        description: `Partial payment of ${parsedAmount.toFixed(2)} ${currency} across all debts${whenLabel}`,
        success_url: successUrl,
        cancel_url: cancelUrl,
        payment_plan_schedule: isScheduled ? buildScheduleForDate(parsedAmount, selectedDate) : undefined,
      });

      if (response.checkout_url) {
        window.location.href = response.checkout_url;
        return;
      }

      throw new Error('Invalid payment response: no checkout URL returned');
    } catch (err) {
      setIsProcessing(false);
      if (err instanceof PaymentApiError) {
        // Account-level checkouts skip the case-level 409 relabel in payment-api,
        // so without this the raw server detail — plan-creation copy — reaches a
        // customer who was only trying to pay.
        setPaymentError(err.status === 409 ? t('payment.planConflict') : err.message);
      } else {
        setPaymentError(t('payment.processingFailed'));
      }
    }
  };

  return (
    <div className="w-full flex-1 min-h-0 stack items-center px-4">
      <div className="flex-1 stack space-y-4 w-full">
        <div className="flex flex-col gap-4">
          {/* Amount Display */}
          <div className="flex flex-col items-center gap-3 mt-14 w-full max-w-sm mx-auto">
            <span className="text-muted-foreground font-semibold">{t('paymentTabs.payPartOfBalance')}</span>
            <div className="relative flex flex-row gap-2 items-center w-full">
              <div className="relative w-full">
                {/* Leading on purpose: this marks what the field accepts, so it stays
                    at the field's edge rather than following the locale's placement. */}
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-muted-foreground">{currencySymbol(currency)}</span>
                <Input
                  type="number"
                  value={partialAmount}
                  onChange={(e) => setPartialAmount(e.target.value)}
                  placeholder="0.00"
                  className="text-center text-5xl! font-bold h-18 pl-10 pr-4 rounded-3xl w-full shadow-xs"
                  min="0"
                  max={totalBalance - 0.01}
                  step="0.01"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">
                  {t('paymentTabs.totalBalance')} <CurrencyDisplay amount={totalBalance} currency={currency} className="text-sm text-foreground font-bold" />
                </span>
                {minPartialPaymentAmount > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground">
                      {t('paymentTabs.minimumPartialHint')} <CurrencyDisplay amount={minPartialPaymentAmount} currency={currency} className="text-sm font-bold" />
                    </span>
                  </div>
                )}
              </div>
              <span className="text-muted-foreground text-center text-sm">
                {t('paymentTabs.combinedTotal')} •{' '}
                <span className="text-foreground font-semibold">
                  {debts.length} {debts.length === 1 ? t('dashboard.debt') : t('dashboard.debts')}
                </span>
              </span>
            </div>
          </div>

          {/* Date Picker */}
          <div className="flex flex-row items-center justify-center gap-3 flex-wrap">
            {/* Date Picker (conditional based on allowDateSelect) */}
            {allowDateSelect ? (
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="text-sm gap-0.5 bg-muted/60 border-border-heavy shadow-none hover:bg-muted rounded-full px-4 py-2 flex flex-row items-center justify-center cursor-pointer!"
                  >
                    <CalendarIcon className="size-4 inline-block mr-2 text-soft-foreground" />
                    <span className="text-sm font-medium">
                      {isToday
                        ? t('paymentTabs.payToday')
                        : selectedDate
                          ? `${t('paymentTabs.payOn')} ${selectedDate.toLocaleDateString()}`
                          : t('paymentTabs.selectDate')}
                    </span>
                    <ChevronDown className="size-4 inline-block ml-2 text-muted-foreground" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto overflow-hidden p-0 rounded-2xl" align="center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    captionLayout="dropdown-months"
                    startMonth={today}
                    endMonth={maxDate}
                    disabled={(date) => {
                      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                      const maxDateOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
                      return dateOnly < todayOnly || dateOnly > maxDateOnly;
                    }}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      setOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-sm gap-0.5 bg-muted rounded-full px-4 py-2 flex flex-row items-center justify-center cursor-default">
                    <CalendarIcon className="size-4 inline-block mr-2 text-muted-foreground" />
                    <span className="text-sm font-medium">{t('paymentTabs.payToday')}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>{t('paymentTabs.partialPaymentsMustBeToday')}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Distribution: chart + per-case rows, fetched from the ledger preview
              only while the accordion is open (nothing renders when closed). */}
          {isValidAmount && (
            <Accordion
              type="single"
              collapsible
              className="rounded-2xl border border-border/60 bg-soft/30 px-4"
              onValueChange={(value) => setDistributionOpen(value === 'distribution')}
            >
              <AccordionItem value="distribution" className="border-b-0">
                <AccordionTrigger className="text-sm hover:bg-transparent">
                  <span className="font-medium text-left">{t('paymentTabs.paymentDistribution')}</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  {previewLoading && (
                    <p className="text-xs text-muted-foreground py-2">{t('common.loading')}</p>
                  )}
                  {previewError && !previewLoading && (
                    <p className="text-xs text-muted-foreground py-2">{t('payment.processingFailed')}</p>
                  )}
                  {!previewLoading && !previewError && previewSegments.length > 0 && (
                    <>
                      <div className="flex justify-center">
                        <PaymentPieChart totalAmount={totalBalance} payingAmount={parsedAmount} segments={previewSegments} currency={currency} />
                      </div>
                      <div className="space-y-1">
                        {previewSegments.map((seg) => (
                          <div key={seg.caseId} className="flex justify-between items-center py-2 border-b border-border/40 last:border-0">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{seg.creditor}</p>
                              <p className="text-xs text-muted-foreground">
                                {seg.percentage.toFixed(1)}
                                {t('paymentTabs.ofTotal')}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <CurrencyDisplay amount={seg.amount} currency={currency} className="text-sm font-semibold" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          {/* Validation Message */}
          {partialAmount && !isValidAmount && (
            <Alert variant="destructive">
              <AlertDescription>
                {isBelowMinimum ? (
                  <>
                    {t('paymentTabs.minimumPartialAmount')} <CurrencyDisplay amount={minPartialPaymentAmount} currency={currency} className="inline-flex gap-0 font-bold" />
                  </>
                ) : (
                  <Trans
                    i18nKey="paymentTabs.enterValidAmount"
                    components={{
                      min: <CurrencyDisplay amount={0.01} currency={currency} className="inline-flex gap-0 font-bold" />,
                      amount: <CurrencyDisplay amount={totalBalance - 0.01} currency={currency} className="inline-flex gap-0 font-bold" />,
                    }}
                  />
                )}
              </AlertDescription>
            </Alert>
          )}

          {needsEmail && <PayerEmailField id="account-partial-payer-email" value={payerEmail} onChange={setPayerEmail} />}

          {/* Warning about remaining balance */}
          {isValidAmount && (
            <Alert variant="warning">
              <CircleAlert />
              <AlertDescription>{t('paymentTabs.willContactRemaining')}</AlertDescription>
            </Alert>
          )}

          {paymentError && (
            <Alert variant="destructive">
              <AlertDescription>{paymentError}</AlertDescription>
            </Alert>
          )}
        </div>

        <AffordabilityCommitment />
      </div>

      <BottomActionBar>
        <Button
          onClick={handleContinue}
          size="xl"
          className="group w-full max-w-sm"
          disabled={!isValidAmount || isProcessing || !emailValid}
        >
          {isValidAmount ? (
            <>
              {selectedDate && !isToday
                ? t('paymentTabs.schedulePayment')
                : t('paymentTabs.payNow', { amount: formatMoneyInLanguage(parsedAmount, currentLanguage, currency) })}
              <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </>
          ) : (
            t('paymentTabs.enterAmount')
          )}
        </Button>
      </BottomActionBar>
    </div>
  );
}
